import { useEffect, useRef, useState } from 'react'
import { Terminal, Trash2, Download, Pause, Play, Filter, Zap } from 'lucide-react'
import { useTradingStore } from '../hooks/useTradingStore'
import clsx from 'clsx'

// Keywords that mark a log as trade-relevant (kept in backtest signal filter)
const TRADE_KEYWORDS = [
  'order', 'fill', 'trade', 'position', 'bracket',
  'buy', 'sell', 'entry', 'exit', 'stop loss', 'take profit',
  'submit', 'close', 'open', 'pnl', 'profit', 'loss',
]

const modeColors: Record<string, string> = {
  live:     'text-green-400 border-green-500/50 bg-green-500/10',
  backtest: 'text-yellow-400 border-yellow-500/50 bg-yellow-500/10',
  idle:     'text-gray-400  border-gray-500/50  bg-gray-500/10',
}

const modeLabels: Record<string, string> = {
  live:     'LIVE',
  backtest: 'BACKTEST',
  idle:     'IDLE',
}


// Log level severity order (higher index = more severe)

//OLD
/*
type LogLevel = 'debug' | 'info' | 'warning' | 'error'
const LOG_LEVEL_ORDER: LogLevel[] = ['debug', 'info', 'warning', 'error']

const levelColors: Record<LogLevel, string> = {
  debug: 'text-gray-400',
  info: 'text-blue-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
}

const levelBgColors: Record<LogLevel, string> = {
  debug: 'bg-gray-500/10',
  info: 'bg-blue-500/10',
  warning: 'bg-yellow-500/10',
  error: 'bg-red-500/10',
}
*/
//New Testing, as it seems to be mixed uppercase or lowercase.. 
type LogLevel = 'DEBUG' | 'debug' | 'INFO' | 'info' | 'WARNING' | 'warning' | 'ERROR' |'error'
const LOG_LEVEL_ORDER: LogLevel[] = ['DEBUG','debug','INFO', 'info', 'WARNING', 'warning', 'ERROR', 'error']

const levelColors: Record<LogLevel, string> = {

  debug: 'text-gray-400',
  info: 'text-blue-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
  DEBUG: 'text-gray-400',
  INFO: 'text-blue-400',
  WARNING: 'text-yellow-400',
  ERROR: 'text-red-400',

}

const levelBgColors: Record<LogLevel, string> = {
  debug: 'bg-gray-500/10',
  info: 'bg-blue-500/10',
  warning: 'bg-yellow-500/10',
  error: 'bg-red-500/10',
  DEBUG: 'bg-gray-500/10',
  INFO: 'bg-blue-500/10',
  WARNING: 'bg-yellow-500/10',
  ERROR: 'bg-red-500/10',

}

export default function LogOutput() {
  const { logs, clearLogs, runMode } = useTradingStore()
  const logContainerRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const [filter, setFilter] = useState<LogLevel | 'all'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [signalOnly, setSignalOnly] = useState(false)

  // Auto-enable signal filter when backtest starts
  useEffect(() => {
    if (runMode === 'backtest') setSignalOnly(true)
    else setSignalOnly(false)
  }, [runMode])

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [logs, autoScroll])

  // Filter logs by minimum level (e.g., "warning" shows warning + error)
  const filteredLogs = logs.filter((log) => {
    if (filter !== 'all') {
      const minLevelIndex = LOG_LEVEL_ORDER.indexOf(filter)
      const logLevelIndex = LOG_LEVEL_ORDER.indexOf(log.level)
      if (logLevelIndex < minLevelIndex) return false
    }
    if (signalOnly) {
      const lvl = log.level.toLowerCase()
      const isImportantLevel = lvl === 'warning' || lvl === 'error'
      const isSystemSource = log.source === 'system'
      const msg = log.message.toLowerCase()
      const isTradeMsg = TRADE_KEYWORDS.some(kw => msg.includes(kw))
      if (!isImportantLevel && !isSystemSource && !isTradeMsg) return false
    }
    if (searchTerm && !log.message.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  // Export logs as JSON
  const exportLogs = () => {
    const dataStr = JSON.stringify(logs, null, 2)
    const blob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `strategy-logs-${new Date().toISOString().slice(0, 19)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp)
      const time = date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      const ms = date.getMilliseconds().toString().padStart(3, '0')
      return `${time}.${ms}`
    } catch {
      return timestamp
    }
  }

  return (
    <div className="card flex flex-col h-full">
      {/* Header */}
      <div className="card-header border-b border-[#2a2a2a] pb-3">
        <div className="flex items-center gap-2">
          <Terminal size={16} className="text-green-400" />
          <h2 className="card-title">Strategy Logs</h2>
          {/* Run mode badge */}
          <span className={clsx(
            'text-xs font-bold border rounded px-1.5 py-0.5',
            modeColors[runMode]
          )}>
            {modeLabels[runMode]}
          </span>
          <span className="text-xs text-[#666] ml-1">
            {filteredLogs.length} / {logs.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Signal-only filter (orders/trades, suppresses bar noise) */}
          <button
            onClick={() => setSignalOnly(v => !v)}
            className={clsx(
              'btn btn-outline text-xs py-1 px-2 flex items-center gap-1',
              signalOnly && 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
            )}
            title={signalOnly ? 'Signal filter ON — showing orders/trades only' : 'Signal filter OFF — showing all logs'}
          >
            <Zap size={12} />
            Signals
          </button>

          {/* Filter Dropdown - minimum log level */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as LogLevel | 'all')}
            className="text-xs bg-[#1a1a1a] border border-[#333] rounded px-2 py-1 text-white"
            title="Minimum log level"
          >
            <option value="all">All Levels</option>
            <option value="debug">Debug+</option>
            <option value="info">Info+</option>
            <option value="warning">Warning+</option>
            <option value="error">Error Only</option>
          </select>

          {/* Auto-scroll toggle */}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={clsx(
              'btn btn-outline text-xs py-1 px-2',
              autoScroll && 'bg-green-500/20 border-green-500/50'
            )}
            title={autoScroll ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
          >
            {autoScroll ? <Play size={14} /> : <Pause size={14} />}
          </button>

          {/* Export */}
          <button
            onClick={exportLogs}
            className="btn btn-outline text-xs py-1 px-2"
            title="Export logs"
            disabled={logs.length === 0}
          >
            <Download size={14} />
          </button>

          {/* Clear */}
          <button
            onClick={clearLogs}
            className="btn btn-outline text-xs py-1 px-2 hover:border-red-500/50 hover:text-red-400"
            title="Clear logs"
            disabled={logs.length === 0}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-[#2a2a2a]">
        <div className="relative">
          <Filter size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#666]" />
          <input
            type="text"
            placeholder="Filter logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-7 py-1.5 text-xs text-white placeholder-[#666] focus:outline-none focus:border-blue-500/50"
          />
        </div>
      </div>

      {/* Log Output */}
      <div
        ref={logContainerRef}
        className="flex-1 overflow-y-auto font-mono text-xs bg-[#0a0a0a] min-h-[200px] max-h-[400px]"
      >
        {filteredLogs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[#666]">
            {logs.length === 0 ? (
              <div className="text-center">
                <Terminal size={32} className="mx-auto mb-2 opacity-50" />
                <p>No logs yet</p>
                <p className="text-[#555] mt-1">Logs will appear when strategy runs</p>
              </div>
            ) : (
              <p>No logs match filter</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#1a1a1a]">
            {filteredLogs.map((log, index) => (
              <div
                key={index}
                className={clsx(
                  'px-3 py-1.5 hover:bg-[#111] flex items-start gap-3',
                  levelBgColors[log.level]
                )}
              >
                <span className="text-[#555] shrink-0 w-20">
                  {formatTimestamp(log.timestamp)}
                </span>
                <span
                  className={clsx(
                    'uppercase font-bold shrink-0 w-14',
                    levelColors[log.level]
                  )}
                >
                  {log.level}
                </span>
                {log.source && (
                  <span className="text-purple-400 shrink-0 w-16 truncate">
                    [{log.source}]
                  </span>
                )}
                <span className="text-[#e0e0e0] flex-1 break-all whitespace-pre-wrap">
                  {log.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Status */}
      <div className="px-3 py-2 border-t border-[#2a2a2a] flex items-center justify-between text-xs text-[#666]">
        <span>
          {autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
        </span>
        <span>
          Last updated: {logs.length > 0 ? formatTimestamp(logs[logs.length - 1].timestamp) : 'N/A'}
        </span>
      </div>
    </div>
  )
}
